// Modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import JSZip from "jszip";

// Components
import { Form } from "antd";
import Modal from "components/molecules/ModalContainer/ModalContainer";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// Type Definitions
import type { UploadFile } from "antd/lib/upload/interface";
import type { OperationTypes } from "components/organisms/EditRejectPageTemplate/EditRejectPageTemplate";

// Constants
import { Routes, EDIT_REJECT_PAGE_TEMPLATE as Constants, FILE_UPLOAD_CONSTANT, REIM_DECLARATION_CONFIG_CODES } from "constants/global";
import { validateAmount, validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";

// Utils
import { getBlobSize, getTotalUploadSize } from "utils/getTotalUploadSize";
import { getFiles } from "utils/GetUrlAsFile";
import { disableFileUpload } from "utils/disableFileUpload";
import { uploadedFileTypeCheck } from "utils/uploadedFileTypeCheck";
import { getFileList } from "utils/getFileList";

// Redux
import {
	rejectReimbursement,
	acceptReimbursement,
	getReimbursementSummarySelector,
	getReimbursementSummary,
	getReimbursementSummaryLoadingSelector,
	updateSelectedVoucherGroupCodeMonthlyDeclarations,
	getLoadingSelector,
	updateFileList,
	getFilesSelector
} from "redux/reimbursement/reimbursementSlice";
import { getConfigData, getConfigSelector } from "redux/userList/userSlice";

// Services
import { ConfigDataItem } from "services/api/types";

export type ReimbursementFieldErrorsType = { reason?: boolean; amount?: boolean };

type FormDataProps = { reimb_summ_id: number; remarks?: string; amount?: number; uploadedFiles: UploadFile<any>[] };

export type LocationState = {
	state: { reimbSummaryId: number; variant: string; tenantRejected?: string };
	pathname: string;
};

export type initialValueTypes = {
	tenant_amount?: number;
	tenant_remarks?: string;
	tenant_documents?: Array<string>;
};

export function useMonthlyReimbursementEditPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();

	const { state } = location as LocationState;
	const reimbSummaryId = state?.reimbSummaryId;
	const variant = state?.variant;
	const isRejectedByTenant = state?.tenantRejected === "X";

	const { data } = useAppSelector(getReimbursementSummarySelector);
	const initialfiles = useAppSelector(getFilesSelector);
	const loadingstate = useAppSelector(getReimbursementSummaryLoadingSelector) === 1;
	const dataLoading = useAppSelector(getLoadingSelector);
	const configDetails = useAppSelector(getConfigSelector);
	const { data: configDetailsData } = configDetails || {};
	const [form] = Form.useForm();
	const [inputFields, setInputFields] = useState<{ reason?: string; amount?: number }>({
		reason: "",
		amount: undefined
	});

	const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
	const [disabled, setDisabled] = useState<boolean>(false);
	const [editable, setEditable] = useState<boolean>(false);
	const [fieldErrors, setFieldErrors] = useState<ReimbursementFieldErrorsType>({
		reason: false,
		amount: false
	});

	useEffect(() => {
		if (reimbSummaryId) {
			dispatch(getReimbursementSummary({ reimb_summ_id: reimbSummaryId }));
			dispatch(getConfigData({ config_mst_code: "7000" }));
		}
	}, [reimbSummaryId]);

	const initialValues: initialValueTypes = useMemo(() => {
		if (data?.reimb_summ && variant === "Edit") {
			setInputFields({ reason: data?.reimb_summ[0]?.tenant_remarks, amount: data?.reimb_summ[0]?.tenant_amount });
			const tenant_details = {
				tenant_amount: data?.reimb_summ?.[0]?.tenant_amount !== 0 ? data?.reimb_summ?.[0]?.tenant_amount : undefined,
				tenant_remarks: data?.reimb_summ?.[0]?.tenant_remarks,
				tenant_documents: data?.reimb_summ?.[0]?.tenant_documents?.length && data?.reimb_summ[0].tenant_documents.split("|")
			};
			dispatch(updateFileList(tenant_details.tenant_documents));
			return tenant_details;
		}
		return {};
	}, [data, loadingstate, setInputFields, reimbSummaryId, dispatch, variant]);

	useEffect(() => {
		if (initialValues.tenant_amount && initialValues.tenant_remarks) {
			setDisabled(true);
			form.setFieldsValue({ Reason: initialValues.tenant_remarks });
			form.setFieldsValue({ Amount: initialValues.tenant_amount });
		}
	}, [initialValues.tenant_amount, initialValues.tenant_remarks, setDisabled]);

	const validateFields = useCallback(() => {
		const fieldsToValidate = [
			{ key: "reason", validator: validateRejectRetroClaimReason },
			{
				key: "amount",
				validator: (value: string) => {
					return parseFloat(value) === 0.0001 || validateAmount(value);
				}
			}
		];

		// Reset error state before validations
		setFieldErrors({
			reason: false,
			amount: false
		});

		const errors: Array<{ field: string | undefined }> = [];

		fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
			const key = field.key as keyof ReimbursementFieldErrorsType;
			const value = inputFields[key];
			if (value) {
				const isValid = field.validator(value.toString());

				if (!isValid) {
					errors.push({ field: field.key });
				}
			}
		});

		if (errors.length) {
			const errs: ReimbursementFieldErrorsType = {};
			errors.forEach((err) => {
				errs[err.field as keyof ReimbursementFieldErrorsType] = true;
			});
			setFieldErrors(errs);
			return;
		}

		return true;
	}, [inputFields, fieldErrors, setFieldErrors]);

	const fieldsEdited = useMemo(() => {
		if (
			(initialValues?.tenant_amount != inputFields.amount || initialValues?.tenant_remarks != inputFields.reason || fileList.length != 0) &&
			(fieldErrors.amount === false || fieldErrors.reason === false)
		) {
			return true;
		}
		return false;
	}, [initialValues.tenant_amount, initialValues.tenant_remarks, inputFields, fileList, fieldErrors.amount, fieldErrors.reason]);

	useEffect(() => {
		validateFields();
	}, [inputFields, fileList]);

	// Check all required fields are entered
	const isNextEnabled = useMemo(() => {
		const values = Object.values(inputFields);
		const errors = Object.values(fieldErrors);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		for (let i = 0; i < errors?.length; i++) {
			if (errors[i] === true) {
				return false;
			}
		}

		if (!fileList?.length) {
			return false;
		}

		return true;
	}, [inputFields, fieldErrors, fileList]);

	const handleFileUpload = useCallback(
		(info: any) => {
			const filelist = getFileList(info);
			setFileList(filelist);
			disableFileUpload(fileList?.length);
		},
		[fileList, setFileList]
	);

	const acceptEndDate = useMemo(() => {
		return configDetailsData?.find((_config: ConfigDataItem) => _config?.config_dtl_code === REIM_DECLARATION_CONFIG_CODES.END_DATE)?.config_dtl_val3;
	}, [configDetails, configDetailsData]);

	const originalAmountHandler = useCallback(() => {
		Modal({
			closable: true,
			type: "confirm",
			title: Constants.PROCEED_ORIGINAL_AMOUNT_LABEL,
			subtitle: `${Constants.PROCEED_ORIGINAL_AMOUNT_SUBLABEL_1}${acceptEndDate}${Constants.PROCEED_ORIGINAL_AMOUNT_SUBLABEL_2}`,
			actionButton: {
				label: Constants.ACCEPT_LABEL,
				onClick: onAccept
			},
			cancelButton: {
				label: Constants.GO_BACK,
				onClick: goBack
			}
		});
	}, [Modal]);

	const handleTextAreaChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setInputFields({ ...inputFields, reason: e.target?.value });
		},
		[inputFields, setInputFields]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (parseFloat(e.target?.value) > 0) {
				setInputFields({ ...inputFields, amount: parseFloat(e.target?.value) });
			} else {
				setInputFields({ ...inputFields, amount: 0.0001 });
			}
		},
		[inputFields, setInputFields]
	);

	const getFormData = async ({ reimb_summ_id, remarks, amount, uploadedFiles }: FormDataProps) => {
		const formData = new FormData();
		formData.append("reimb_summ_id", reimb_summ_id.toString());
		remarks && formData.append("remarks", remarks);
		amount && formData.append("amount", amount === 0.0001 ? "0" : amount.toString());

		const zip = new JSZip();

		/**
		 * @description To avoid CORS error on upload due to AWS WAF policy, we need to add a dummy text file of 5kB size
		 * This file will be removed by Backend based on the filename dummy.txt
		 */
		zip.file("dummy.txt", new Blob(["Welcome to CAG".repeat(360)], { type: "text/plain;charset=utf-8" }));

		if (uploadedFiles?.length) {
			const numberOfFiles = uploadedFiles?.length;
			for (let i = 0; i < numberOfFiles; i++) {
				const blobData = uploadedFiles[i]?.url ? await getFiles(uploadedFiles[i]?.url) : (uploadedFiles[i]?.originFileObj as Blob);
				/**
				 * Initialized to a dummy filename with extension to avoid BE error during unzipping
				 */
				let urlFileName = `urlFileName-${i}.jpg`;
				if (uploadedFiles[i]?.url) {
					const lastIndexVal: number = uploadedFiles[i]?.url?.lastIndexOf(".") ?? 0;
					const fileType = uploadedFiles[i]?.url?.substring(lastIndexVal + 1);
					urlFileName = `filename-${i}.${fileType}`;
				}
				const filename = uploadedFiles[i]?.url ? urlFileName : uploadedFiles[i]?.name;
				zip.file(filename, blobData as Blob);
			}
		} else {
			for (let i = 0; i < initialfiles?.length; i++) {
				const blobData = await getFiles(initialfiles?.[i]);
				const fileType = initialfiles?.[i].substring(initialfiles?.[i].lastIndexOf(".") + 1);
				zip.file(`filename-${i}.${fileType}`, blobData as Blob);
			}
		}

		// Generate the complete zip file
		const zipFile = await zip
			.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 9 } })
			.then((blobdata) => {
				return new Blob([blobdata]);
			})
			.catch(() => {
				return new Blob();
			});

		formData.append("files", zipFile);

		return formData;
	};

	const editApiHandler = async (inputs: { reason?: string; amount?: number }, uploadedFiles: UploadFile<any>[]) => {
		const apiPayload = await getFormData({ reimb_summ_id: reimbSummaryId, remarks: inputs.reason, amount: inputs.amount, uploadedFiles: uploadedFiles });
		try {
			const { errors, message } = await dispatch(rejectReimbursement(apiPayload)).unwrap();
			if (!errors?.length && message) {
				CountDownModal({
					timer: 3,
					title: Constants.CLAIM_REJECTED_LABEL,
					subtitle: Constants.CLAIM_REJECTED_SUBLABEL,
					onTimeComplete: goBack,
					okButton: {
						label: Constants.GOT_IT_LABEL,
						onClick: goBack
					},
					closable: true
				});
			}
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: err?.response?.data?.errors?.[0]?.message,
					onTimeComplete: goBack
				});
			}
		}
	};

	const handleEditSubmission = useCallback(() => {
		setDisabled(false);
		setEditable(true);
	}, [setDisabled, setEditable]);

	const handleSubmit = useCallback(
		async (operationType?: OperationTypes) => {
			if (operationType === "edit") {
				const totalFileSize: number = await getTotalUploadSize(fileList);
				const isFileValid = await uploadedFileTypeCheck(FILE_UPLOAD_CONSTANT.EDIT_MONTHLY_REIMBURSMENT_FILETYPE, fileList);

				if (totalFileSize > 5 || !isFileValid) {
					CountDownModal({
						timer: 3,
						title: !isFileValid ? FILE_UPLOAD_CONSTANT.UNSUPPORTED_FILE_TYPE : FILE_UPLOAD_CONSTANT.MAXIMUM_FILE_LIMIT,
						errorIcon: true
					});
				} else {
					editApiHandler(inputFields, fileList);
				}
			}
		},
		[inputFields, fileList, editApiHandler, CountDownModal, getTotalUploadSize, uploadedFileTypeCheck]
	);

	const goBack = useCallback(() => {
		dispatch(updateSelectedVoucherGroupCodeMonthlyDeclarations({}));
		navigate(Routes.MONTHLY_DECLARATIONS, { state: { reload: true } });
	}, [navigate, dispatch]);

	const onAccept = useCallback(async () => {
		try {
			const Apipayload = { reimb_summ_id: reimbSummaryId };
			const { errors, message } = await dispatch(acceptReimbursement(Apipayload)).unwrap();
			if (!errors?.length && message)
				CountDownModal({
					timer: 3,
					title: Constants.DECLARATION_CHANGED_LABEL,
					onTimeComplete: () => {
						dispatch(getReimbursementSummary({ reimb_summ_id: reimbSummaryId }));
						goBack();
					}
				});
		} catch (error) {
			const err = error as any;
			if (err?.response?.data?.errors) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: err?.response?.data?.errors?.[0]?.message,
					onTimeComplete: goBack
				});
			}
		}
	}, [goBack, dispatch]);

	const handleChange = useCallback(async () => {
		const totalFileSize: number = await getTotalUploadSize(fileList);
		const existingFileSize: number = await getBlobSize(initialfiles);
		const isFileValid = await uploadedFileTypeCheck(FILE_UPLOAD_CONSTANT.EDIT_MONTHLY_REIMBURSMENT_FILETYPE, fileList);

		if (totalFileSize + existingFileSize > 5 || !isFileValid) {
			CountDownModal({
				timer: 3,
				title: !isFileValid ? FILE_UPLOAD_CONSTANT.UNSUPPORTED_FILE_TYPE : FILE_UPLOAD_CONSTANT.MAXIMUM_FILE_LIMIT,
				errorIcon: true
			});
		} else {
			const apiPayload = await getFormData({ reimb_summ_id: reimbSummaryId, remarks: inputFields.reason, amount: inputFields.amount, uploadedFiles: fileList });
			try {
				const { errors } = await dispatch(rejectReimbursement(apiPayload)).unwrap();
				if (!errors?.length) {
					CountDownModal({
						timer: 3,
						title: Constants.CHANGES_MADE_LABEL,
						onTimeComplete: () => {
							dispatch(getReimbursementSummary({ reimb_summ_id: reimbSummaryId }));
							goBack();
						}
					});
				}
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: err?.response?.data?.errors?.[0]?.message,
						onTimeComplete: goBack
					});
				}
			}
		}
	}, [goBack, getFormData, getTotalUploadSize, uploadedFileTypeCheck, CountDownModal, dispatch, getBlobSize, initialfiles, inputFields, fileList]);

	return {
		inputFields,
		isNextEnabled,
		disabled,
		editable,
		initialValues,
		goBack,
		form,
		handleFileUpload,
		handleTextAreaChange,
		handleInputChange,
		handleSubmit,
		handleChange,
		handleEditSubmission,
		originalAmountHandler,
		onAccept,
		fieldErrors,
		fieldsEdited,
		loadingstate,
		loadingWithoutData: loadingstate && !data,
		isRejectedByTenant,
		submissionLoading: dataLoading === 1
	};
}
